// dates.js — normalización de fechas tecleadas a ISO (YYYY-MM-DD).
//
// Los modales de socio usan un input de texto enmascarado (DD/MM/AAAA), no un
// <input type="date">, así que el usuario puede salir del campo a medio escribir.
// La conversión anterior partía por '/' y montaba `${y}-${m}-${d}` SIN validar nada:
// quien tecleaba `01/01/199` y tabulaba generaba **'199-01-01'**, que el backend
// rechazaba. Eso producía tres issues distintos en Sentry producción (birth_date,
// enrollment_date y expiration_date) y, en el PUT, un 500 opaco.
//
// Aquí la regla es explícita: si no es una fecha REAL y completa, no se inventa.

/** Centinela de "no se pudo interpretar". Los llamantes lo usan para decidir si mandan el campo. */
export const DATE_FALLBACK = '2000-01-01';

/**
 * ¿Son año/mes/día una fecha que existe de verdad?
 * Descarta 31/02, meses 13, y —lo que provocaba el bug— años que no son de 4 cifras.
 */
export function isRealDate(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < 1000 || year > 9999) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

/**
 * Convierte lo tecleado a ISO `YYYY-MM-DD`.
 * Devuelve `null` si está vacío o no se puede interpretar — el llamante decide
 * si eso es un error de usuario o si aplica un valor por defecto.
 */
export function toIsoDate(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (s === '') return null;

  // Ya viene en ISO: validar que además sea una fecha real (2026-02-31 no lo es).
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return isRealDate(Number(y), Number(m), Number(d)) ? s : null;
  }

  // Formato del input enmascarado: DD/MM/AAAA. El año DEBE tener 4 dígitos.
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, d, m, y] = slash;
    if (!isRealDate(Number(y), Number(m), Number(d))) return null;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Cualquier otra cosa con '/' (incompleta, año corto…) NO se interpreta.
  if (s.includes('/')) return null;

  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().split('T')[0];
}

/**
 * Igual que `toIsoDate` pero devolviendo el centinela en vez de `null`, para los
 * llamantes que ya razonan comparando contra `DATE_FALLBACK`.
 */
export function toIsoDateOrFallback(input) {
  return toIsoDate(input) ?? DATE_FALLBACK;
}

/**
 * ¿El usuario escribió algo que NO es una fecha válida?
 * Un campo vacío no es un error (es opcional); uno a medio escribir sí.
 */
export function isIncompleteDate(input) {
  if (input == null) return false;
  const s = String(input).trim();
  if (s === '') return false;
  return toIsoDate(s) === null;
}

// ── Cumpleaños (petición Kong Club, 15-ago) ─────────────────────────────────
//
// Vive AQUÍ, y no en cada pantalla, por la regla de la casa: un dato, un helper.
// El perfil del socio, el check-in y el aviso del POS tienen que responder todos
// lo mismo a "¿hoy es su cumple?" — tres `slice(5,10)` inline serían tres verdades.

/**
 * ¿Hoy es el cumpleaños del socio? (compara SOLO mes y día).
 *
 * Acepta ISO 'YYYY-MM-DD' (lo que manda la API), Date, o lo tecleado en el modal.
 * Devuelve false si no hay fecha — la de nacimiento es opcional en el alta y en la
 * preinscripción, así que la mayoría de socios no la tienen: sin fecha, no hay aviso.
 *
 * Ojo con la zona horaria, que es donde esto se rompe siempre: `new Date('1990-08-15')`
 * se parsea como MEDIANOCHE UTC, y al leerlo en local con desfase negativo cae en el
 * día 14 — el socio se quedaría sin felicitación justo el día bueno. Por eso el mes y
 * el día se sacan del string, sin construir un Date, y "hoy" se toma en hora local.
 *
 * 29 de febrero: en los años no bisiestos se felicita el 28 (el 29 no existe y quedarse
 * sin cumple tres de cada cuatro años no es lo que quiere el club).
 */
export function isBirthdayToday(value, today = new Date()) {
  const iso = toIsoDate(value instanceof Date && !Number.isNaN(value.getTime())
    ? value.toISOString().split('T')[0]
    : value);
  if (!iso) return false;

  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  if (month === todayMonth && day === todayDay) return true;

  // 29-feb en año no bisiesto → se celebra el 28.
  if (month === 2 && day === 29 && todayMonth === 2 && todayDay === 28) {
    return !isRealDate(today.getFullYear(), 2, 29);
  }
  return false;
}

/**
 * Años que cumple HOY, o null si no se puede calcular. Solo para el texto del aviso
 * ("cumple 34"), nunca para decidir si se muestra: eso lo manda `isBirthdayToday`.
 */
export function birthdayAge(value, today = new Date()) {
  const iso = toIsoDate(value instanceof Date && !Number.isNaN(value.getTime())
    ? value.toISOString().split('T')[0]
    : value);
  if (!iso) return null;
  const age = today.getFullYear() - Number(iso.slice(0, 4));
  return age > 0 && age < 130 ? age : null;
}
